import { useMemo } from "react";
import { ReportTemplatesView } from "@common/types/views/reportTemplatesView";
import { View } from "@common/types/views/views";
import { pluralizeWithCount } from "@common/helpers/string";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useReportTemplates } from "@api/reportTemplate.api";
import { ArchiveFilter } from "../templates/archiveFilter";
import { ReportTemplateMenuItemKey } from "./reportTemplateActions";
import { filterReportTemplates } from "./reportTemplatesFilters";
import {
    ReportTemplateRow,
    reportTemplatesViewConfig,
} from "./reportTemplatesViewConfig";

export interface ReportTemplateCallbacks {
    onApply: (template: ReportTemplateRow) => void;
    onViewApplied: (template: ReportTemplateRow) => void;
    onMenuItemSelect: (selection: {
        key: ReportTemplateMenuItemKey;
        template: ReportTemplateRow;
    }) => void;
}

interface Props {
    search: string;
    archiveFilter: ArchiveFilter;
    view: ReportTemplatesView;
    canManage: boolean;
    callbacks: ReportTemplateCallbacks;
    onViewChange: (view: View) => void;
    onClearFilters: () => void;
}

export const ReportTemplatesList = ({
    search,
    archiveFilter,
    view,
    canManage,
    callbacks,
    onViewChange,
    onClearFilters,
}: Props) => {
    const {
        data: templates,
        isFetched,
        isError,
        refetch,
    } = useReportTemplates();

    const rows = useMemo<ReportTemplateRow[]>(
        () =>
            filterReportTemplates({
                templates: (templates ?? []).map((template) => ({
                    ...template,
                    id: template.id ?? "kick",
                    templateId: template.id,
                })),
                search,
                archiveFilter,
            }),
        [templates, search, archiveFilter],
    );

    const tableMeta = useMemo<
        ViewTableMeta<ReportTemplateCallbacks, { canManage: boolean }>
    >(() => ({ callbacks, options: { canManage } }), [callbacks, canManage]);

    return (
        <ViewTable
            data={isFetched ? rows : null}
            view={view}
            config={reportTemplatesViewConfig}
            onViewChange={onViewChange}
            emptyView={() =>
                isError ? (
                    <EmptyState
                        header="Failed to load templates"
                        ctaText="Retry"
                        action={() => void refetch()}
                    />
                ) : (
                    <EmptyState
                        header="No templates found"
                        body="No templates match your search or filters."
                        ctaText="Clear filters"
                        action={onClearFilters}
                    />
                )
            }
            loadingView={() => <Loader />}
            tableMeta={tableMeta}
            fixedTopOffsetPx
            enableColumnResizing
            footer={
                <CommandIsland
                    sticky
                    className={commandIslandStyles.bulkActionsStickyFooter}
                >
                    {rows.length > 0 ? (
                        <CommandIslandTotal>
                            {pluralizeWithCount("template", rows.length)}
                        </CommandIslandTotal>
                    ) : undefined}
                </CommandIsland>
            }
        />
    );
};
